// Record pages: the company page on RecordPage (record-page redesign step 11;
// docs/record-page-redesign.md "### Company · seller / prospect", "### Ownership &
// acquisition", "### Marketing materials", "### MyExit", build plan 11).
// CompanyDetail (views/company_detail.jsx) keeps every fetch, save path and
// action-drawer state and renders <window.CompanyPageV2 …/> on the v2 surface;
// this file owns the v2-only hooks (summary, panel param, draft flags, blast
// state, research) and the layout. Cross-file names are read from window at
// render time, never at module evaluation. The pure helpers are
// window.CompanyPageKit (test/company_page_v2.test.mjs).
//
// The roster buyer-firm variant is step 12's: its seams (S0–S6, marked
// "firm variant (step 12)") are written here in their final form and read
// window.BuyerFirmV2 at render; until that file ships a roster firm shows the
// column skeleton. Step 12 edits nothing in this file.

const V2_DIM = { color: 'var(--text-4)' };
const V2_LABEL_W = 110;
const V2_LINK = { background: 'transparent', border: 'none', padding: 0, cursor: 'pointer', color: 'var(--accent-text)', fontSize: 11.5, fontFamily: 'inherit' };
const V2_PILL_BTN = { all: 'unset', cursor: 'pointer', display: 'inline-flex' };
const V2_ROW = { display: 'flex', alignItems: 'center', gap: 8, padding: '6px 14px', minWidth: 0 };
const V2_ELLIPSIS = { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 };
const V2_ICON_LINK = { display: 'inline-flex', alignItems: 'center', padding: 2, color: 'var(--accent-text)', lineHeight: 0 };
const V2_LINE_BTN = { background: 'transparent', border: 'none', padding: '8px 14px', cursor: 'pointer', color: 'var(--text-2)', font: 'inherit', fontSize: 12,
  display: 'flex', alignItems: 'center', gap: 6, width: '100%', textAlign: 'left', minWidth: 0 };
const V2_SUBHEAD = { padding: '6px 14px', fontSize: 11, color: 'var(--text-3)', borderBottom: '1px solid var(--border)' };
const V2_FOLD_BTN = { display: 'flex', alignItems: 'center', gap: 6, width: '100%', padding: '7px 14px', background: 'var(--surface-2)', border: 'none',
  borderBottom: '1px solid var(--border)', cursor: 'pointer', textAlign: 'left', fontSize: 11, color: 'var(--text-3)', fontFamily: 'inherit' };
const V2_H1 = { fontSize: 16, fontWeight: 600, color: 'var(--text)' };
const V2_AKA = { fontSize: 13, color: 'var(--text-3)', fontWeight: 500 };
const V2_META = { display: 'inline-flex', alignItems: 'center', gap: 6, minWidth: 0 };
const PEOPLE_SHOWN = 5;
const FILES_SHOWN = 3;
// = company_detail.jsx OWN_HUE (private there).
const OWN_HUE = { privately_held: 'green', retarget: 'amber', dq: 'rose', unsure: 'slate' };
// = the classic header's RoleChips details (titles, not body text).
const ROLE_DETAILS = {
  seller: 'Has a seller-side engagement — see the Engagements panel.',
  buyer: 'Buyer firm — appears in buyer pursuits / the buyer directory.',
  referral_partner: 'Employs an active referral partner (Brand Builder list).',
};
const FIELD_LABEL = { industry_id: 'Industry', hq_city: 'HQ city', hq_state: 'HQ state', website: 'Website', revenue_est: 'Revenue', ebitda_est: 'EBITDA' };
const MARKETING_FIELDS = [['blind_descriptor', 'Blind descriptor'], ['pb_descriptor', 'PB/PEG descriptor'], ['unblinded_notes', 'Unblinded notes']];
const MILESTONE_TIP = 'A milestone is a stage the company has evidence of reaching: a note, an imported stage date or a deal.';

// ── CompanyPageKit (pure; atlas-crm/test/company_page_v2.test.mjs) ───────────
const CARD_IDS = Object.freeze({
  seller: ['company.deals', 'company.interested_buyers', 'company.seller_brief', 'company.intel', 'company.comps', 'company.vault', 'company.marketing', 'company.myexit', 'company.calls'],
  non_seller: ['company.deals', 'company.files', 'company.calls'],
  buyer_firm: ['company.pursuits', 'company.passes', 'company.firm', 'company.deals', 'company.files', 'company.calls'],   // step 12 fills the cards
  company: [],
});
const TERMINAL = new Set(['won', 'ss_won', 'buyer_closed']);
// The rail strip per card id (icon names resolve on window.Icon at render; panel = the drawer it opens).
const RAIL_META = Object.freeze({
  'company.deals': { label: 'Deals', icon: 'Pipeline', panel: null },
  'company.interested_buyers': { label: 'Buyers', icon: 'Users', panel: null },
  'company.seller_brief': { label: 'Company intel', icon: 'Building', panel: 'intel' },
  'company.comps': { label: 'Market', icon: 'ChartBar', panel: 'market' },
  'company.vault': { label: 'Data vault', icon: 'Folder', panel: 'vault' },
  'company.marketing': { label: 'Marketing', icon: 'Mail', panel: 'marketing' },
  'company.myexit': { label: 'MyExit', icon: 'Target', panel: 'myexit' },
  'company.calls': { label: 'Calls', icon: 'Phone', panel: 'calls' },
  'company.files': { label: 'Files', icon: 'Doc', panel: 'files' },
});
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const fmtDay = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return `${MONTHS[d.getMonth()]} ${d.getDate()}`;
};
const moneyOf = (n, fmt) => (typeof fmt === 'function' ? fmt(n) : (window.fmtMoney ? window.fmtMoney(n) : String(n)));
const blastPillText = (q) => `${String(q.list).toUpperCase()} ${q.pushed ? 'in SuperSend' : 'queued'}`;
// 'PB queued' / 'PEG in SuperSend' while a list is queued, else 'Sent <day>' from the sent history, else null.
const blastState = (blast) => {
  if (!blast) return null;
  const q = (blast.queued || []).map((x) => blastPillText({ list: x.list_type, pushed: !!x.pushed_at }));
  if (q.length) return q.join(' · ');
  const sent = Object.values(blast.last_sent || {}).map((v) => (v && typeof v === 'object' ? (v.sent_at || v.at || null) : v)).filter(Boolean).sort().pop();
  return sent ? `Sent ${fmtDay(sent)}` : null;
};
const rankAdapted = (contacts) => [...contacts].sort((a, b) => {
  const ra = a._raw || {}; const rb = b._raw || {};
  return ((rb.is_decision_maker ? 1 : 0) - (ra.is_decision_maker ? 1 : 0))
    || String(b.lastInteractionAt || rb.last_interaction_at || '').localeCompare(String(a.lastInteractionAt || ra.last_interaction_at || ''))
    || String(ra.last_name || a.name || '').toLowerCase().localeCompare(String(rb.last_name || b.name || '').toLowerCase());
});
const CompanyPageKit = Object.freeze({
  CARD_IDS,
  PEOPLE_SHOWN,
  variantCards: (variant) => CARD_IDS[variant] || [],
  // Header pills in priority order: roles · one ownership slot · DNC · one per queued blast list.
  pills: ({ roles, isAcquired, soldThroughUs, pending, dnc, blast }) => {
    const out = [];
    if (roles && roles.length) out.push({ key: 'roles' });
    if (isAcquired) out.push({ key: soldThroughUs ? 'sold' : 'acquired' });
    else if (pending > 0) out.push({ key: 'pending', n: pending });
    if (dnc) out.push({ key: 'dnc' });
    ((blast && blast.queued) || []).forEach((q) => out.push({ key: 'blast', list: q.list_type, pushed: !!q.pushed_at }));
    return out;
  },
  blastPillText,
  blastState,
  // 'Live: <deal> · <stage>' + ' · +N live' from the live own deals; null when none.
  liveMeta: (liveEngs) => {
    const live = liveEngs || [];
    if (!live.length) return null;
    const first = live[0];
    return { id: first.id, text: `Live: ${first.name}${first.stageName ? ` · ${first.stageName}` : ''}`, more: live.length - 1 };
  },
  dealsHeader: ({ engagements, liveEngs, totalEngFees }, fmt) => (engagements
    ? `${(liveEngs || []).length} live · ${moneyOf(totalEngFees || 0, fmt)} pipeline` : '—'),
  dealsGroups: ({ engagements, dealEngs, liveEngs, archivedEngs, buyerEngs }) => {
    const live = liveEngs || [];
    const own = dealEngs || [];
    return {
      live,
      closed: own.filter((e) => !e.archived && TERMINAL.has(e.stage)),
      other: own.filter((e) => !e.archived && !TERMINAL.has(e.stage) && !live.includes(e)),   // exited: listed dim under live
      archived: archivedEngs || [],
      shells: (engagements || []).filter((e) => e.type === 'buyer_buyside'),
      buying: buyerEngs || [],
    };
  },
  showMarketing: ({ sellerTools, liveSeller, raw, blast }) => !!sellerTools && (!!liveSeller
    || !!(raw && (raw.blind_descriptor || raw.pb_descriptor || raw.unblinded_notes))
    || !!(blast && ((blast.queued || []).length || Object.keys(blast.last_sent || {}).length))),
  marketingLine: ({ raw, blast }) => [
    `Blind ${raw && raw.blind_descriptor ? '✓' : '–'}`, `PB/PEG ${raw && raw.pb_descriptor ? '✓' : '–'}`,
    `Unblinded ${raw && raw.unblinded_notes ? '✓' : '–'}`, blastState(blast)].filter(Boolean),
  showMyExit: ({ sellerTools, liveSeller, myexit }) => !!sellerTools && (!!liveSeller || !!(myexit && myexit.runs > 0)),
  myexitLine: (myexit, label) => {
    if (!myexit) return null;
    const lab = typeof label === 'function' ? label : (k) => k;
    const s = (myexit.latest && myexit.latest.summary) || {};
    const done = !!(myexit.latest && myexit.latest.status === 'completed');
    const pills = done ? [s.archetype ? lab(s.archetype) : null, s.timing ? lab(s.timing) : null, s.confidence != null ? `${s.confidence}%` : null].filter(Boolean) : [];
    return { runs: myexit.runs || 0, pills, inProgress: !!(myexit.latest && myexit.latest.status === 'in_progress') };
  },
  showCalls: (calls) => !!calls && (calls.count > 0 || !!calls.do_not_call || !!calls.bad_phone),
  callsLine: (calls) => (!calls || !calls.count ? '0 calls'
    : `${calls.count} call${calls.count === 1 ? '' : 's'}${calls.last ? ` · last ${calls.last.outcome_label || calls.last.outcome || '—'}${calls.last.at ? ` ${fmtDay(calls.last.at)}` : ''}` : ''}`),
  ownershipPill: ({ ownership, acquisitions, isAcquired, soldThroughUs }) => {
    if (isAcquired) return { text: soldThroughUs ? 'Sold through us' : `Acquired${ownership && ownership.acquired_by ? ` by ${ownership.acquired_by}` : ''}`, hue: soldThroughUs ? 'green' : 'rose' };
    const n = acquisitions && acquisitions.pending;
    if (n > 0) return { text: `${n} to confirm`, hue: 'amber' };
    if (ownership && ownership.outcome_label) return { text: [ownership.outcome_label, ownership.reason_label].filter(Boolean).join(' · '), hue: OWN_HUE[ownership.effective_outcome] || 'slate' };
    return null;
  },
  // = company_detail.jsx isLiveDeal: an own deal that is not stale, archived or in a terminal stage.
  liveDeal: (e) => !!e && e.type !== 'buyer_buyside' && !e.stale && !e.archived && !TERMINAL.has(e.stage),
  // ⋯ items as keys, in order, with dividers as '|' (the page maps keys → labels / handlers).
  menuKeys: ({ variant, myexitCardShown }) => {
    if (variant === 'seller') return ['new_engagement', 'add_contact', 'send_on_blast', 'history', '|', 'mark_acquired', 'not_acquired',
      ...(!myexitCardShown ? ['start_myexit', 'upload_myexit'] : [])];
    if (variant === 'buyer_firm') return ['add_contact', 'new_engagement', 'edit_profile', 'history'];
    return ['new_engagement', 'add_contact', 'history'];   // non_seller and the unknown 'company' variant
  },
  keyFactsPreview: (c) => [c.owner, c.industry, c.hq].map((x) => (x && x !== '—' ? x : null)).filter(Boolean).join(' · ') || '—',
  // The top 5 adapted rows: through `rank` (DealPageKit.rankPeople over the raw rows) when it exists,
  // else decision-makers → last touch desc → last name.
  peopleRows: (contacts, rank) => {
    if (!Array.isArray(contacts)) return [];
    let ranked;
    if (typeof rank === 'function') {
      const byId = new Map(contacts.map((c) => [String(c.id), c]));
      ranked = rank(contacts.map((c) => (c && c._raw ? c._raw : c)), null).map((r) => byId.get(String(r.id))).filter(Boolean);
    } else {
      ranked = rankAdapted(contacts);
    }
    return ranked.slice(0, PEOPLE_SHOWN);
  },
  railItems: (variant) => (CARD_IDS[variant] || []).map((id) => (RAIL_META[id] ? { key: id, ...RAIL_META[id] } : null)).filter(Boolean),
});
// = CompanyDetail.toggleAcquired's confirm (company_detail.jsx), word for word: marking a company acquired
// exits every live deal as Company sold (close_out_company), so both pages ask the same question first.
const confirmExitLiveDeals = (actives) => {
  const first = actives[0];
  return window.showConfirm({
    title: actives.length === 1 ? 'Mark acquired and exit the deal?' : 'Mark acquired and exit the deals?',
    body: actives.length === 1
      ? `"${first.name}" is still live${first.stageName ? ` at ${first.stageName}` : ''}. Marking the company acquired exits it as "Company sold" and logs a note on the company and the deal.`
      : `This company has ${actives.length} live engagements. Marking it acquired exits them all as "Company sold" and logs a note on the company.`,
    confirmLabel: actives.length === 1 ? 'Mark acquired & exit deal' : 'Mark acquired & exit deals',
    danger: true,
  });
};

// ── building blocks ───────────────────────────────────────────────────────────
// One Key-facts row (the classic Row's look: 110px label, value flex, dimmed '—'); `after` = the research wand.
const KeyRow = ({ label, value, after }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 14px', minHeight: 30 }}>
    <span style={{ width: V2_LABEL_W, fontSize: 11.5, color: 'var(--text-3)', flexShrink: 0 }}>{label}</span>
    <span style={{ flex: 1, minWidth: 0, fontSize: 12, color: 'var(--text)', display: 'inline-flex', alignItems: 'center', gap: 5, overflow: 'hidden', textOverflow: 'ellipsis' }}>
      {value == null || value === '' || value === '—' ? <span style={V2_DIM}>—</span> : value}
      {after}
    </span>
  </div>
);
const RailFooter = ({ onClick }) => (
  <div style={{ padding: '6px 14px 10px' }}>
    <button type="button" data-all-details="" onClick={onClick} style={V2_LINK}>All details ›</button>
  </div>
);
// The dimmed 40px rows of a column whose variant is not known yet (or a roster firm before step 12).
const ColumnSkeleton = () => (
  <window.Section variant="rail">
    <div data-column-skeleton="" style={{ padding: '10px 14px' }}><window.SkeletonRows rows={3} /></div>
  </window.Section>
);
// A one-line card: a collapsible whose preview is the line and whose open body is the same line → the drawer.
const LineCard = ({ id, title, line, onOpen, action = null, children = null }) => (
  <window.SummaryCard title={title} action={action} collapsible id={id} preview={line}>
    <button type="button" onClick={onOpen || undefined} disabled={!onOpen} style={{ ...V2_LINE_BTN, cursor: onOpen ? 'pointer' : 'default' }}>
      <span style={V2_ELLIPSIS}>{line}</span>
      {children}
    </button>
  </window.SummaryCard>
);
// The RelatedEngagementsPanel fold (a rotating chevron + the rows when open).
const Fold = ({ label, open, onToggle, hidden = false, children }) => (hidden ? null : (
  <>
    <button type="button" onClick={onToggle} aria-expanded={open} style={V2_FOLD_BTN}>
      <window.Icon.ChevronR size={10} style={{ transform: open ? 'rotate(90deg)' : 'none' }} />
      <span style={{ fontWeight: 600 }}>{label}</span>
    </button>
    {open && children}
  </>
));
// A 'Buying into' row (the BuyerEngagementsPanel row body): the deal, its target, the pursuit pills.
const BUYING_NDA_HUE = { signed: 'green', sent: 'amber', redlined: 'amber', declined: 'rose', not_sent: 'slate' };
const BUYING_INT_HUE = { high: 'green', medium: 'amber', low: 'slate', passed: 'rose' };
const BuyingRow = ({ b, onOpen }) => (
  <button type="button" onClick={onOpen} style={{ ...V2_LINE_BTN, padding: '8px 14px', borderBottom: '1px solid var(--border)', alignItems: 'center' }}>
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{ ...V2_ELLIPSIS, fontSize: 12.5, fontWeight: 600, color: 'var(--text)' }}>{b.engagement_name}</div>
      {b.seller_company_name && <div style={{ ...V2_ELLIPSIS, fontSize: 11, color: 'var(--text-3)' }}>Target · {b.seller_company_name}</div>}
      <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 2, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {b.stage_name && <window.Pill hue="slate" sz="sm">{b.stage_name}</window.Pill>}
        {b.interest_level && <window.Pill hue={BUYING_INT_HUE[b.interest_level] || 'slate'} sz="sm">{b.interest_level}</window.Pill>}
        {b.nda_status && b.nda_status !== 'not_sent' && <window.Pill hue={BUYING_NDA_HUE[b.nda_status] || 'slate'} sz="sm">{`NDA ${b.nda_status}`}</window.Pill>}
        {b.engagement_archived && <window.Pill hue="slate" sz="sm">archived</window.Pill>}
      </div>
    </div>
    <window.Icon.ChevronR size={12} color="var(--text-4)" />
  </button>
);
// The Source & status Ownership value: the verdict pill → the Ownership drawer; dimmed '—' when nothing is known.
const OwnershipValue = ({ summary, company, soldThroughUs, onClick }) => {
  const raw = company._raw || {};
  const s = (summary && summary.data) || {};
  const pill = summary && summary.loading ? null
    : CompanyPageKit.ownershipPill({ ownership: s.ownership, acquisitions: s.acquisitions, isAcquired: !!raw.is_acquired, soldThroughUs });
  if (!pill) return <span data-ownership-value="" style={V2_DIM}>—</span>;
  return (
    <button type="button" data-ownership-value="" onClick={onClick} style={V2_PILL_BTN}>
      <window.Pill hue={pill.hue} sz="sm">{pill.text}</window.Pill>
    </button>
  );
};

// ── v2-only hook: the blast queue state (one cached GET; re-read after a write) ──
const useBlastState = (companyId) => {
  const [state, setState] = React.useState(null);
  React.useEffect(() => {
    if (!companyId) { setState(null); return undefined; }
    let alive = true;
    const load = () => window.AtlasApi.blastCompanyState(companyId)
      .then((r) => { if (alive) setState(r || null); })
      .catch(() => { if (alive) setState(null); });
    load();
    // api.jsx announces { root: '/api/<seg>' } for the roots in _RECORD_WRITE_ROOTS (a queue, a company field).
    const onWrite = (e) => { const d = (e && e.detail) || {}; if (!d.root || d.root === '/api/blast' || d.root === '/api/companies') load(); };
    window.addEventListener('atlas-record-write', onWrite);
    window.addEventListener('atlas-data-changed', load);
    return () => { alive = false; window.removeEventListener('atlas-record-write', onWrite); window.removeEventListener('atlas-data-changed', load); };
  }, [companyId]);
  return state;
};

// ── left: Key facts → People → Source & status (owns the research controller) ──
const CompanyLeft = ({ company, users, contacts, stateOptions, industryOptions, sellerTools, soldThroughUs, summary, onSave, setContactId, onAddContact, openPanel }) => {
  const rs = window.useFieldResearch('company', company.id);
  const EditableCell = window.EditableCell;
  const raw = company._raw || {};
  const dcFields = raw.dc_synced_fields || [];
  const dcLock = (f) => dcFields.includes(f);
  const canWrite = window.recordCanWrite();
  const fmtMoney = window.fmtMoney;
  const wand = (field, kind, value, onApply, display) => (
    <window.ResearchField ctl={rs} field={field} label={FIELD_LABEL[field]} kind={kind} value={value} display={display} onApply={onApply} />
  );
  const s = (summary && summary.data) || {};
  const ownPill = sellerTools && !summary.loading
    ? CompanyPageKit.ownershipPill({ ownership: s.ownership, acquisitions: s.acquisitions, isAcquired: !!raw.is_acquired, soldThroughUs }) : null;
  const statusPreview = [company.revenue != null ? fmtMoney(company.revenue) : null, company.pipelineState || null, ownPill ? ownPill.text : null].filter(Boolean).join(' · ') || '—';
  return (
    <>
      <window.Section variant="rail" title="Key facts" collapsible id="company.key_facts" preview={CompanyPageKit.keyFactsPreview(company)}>
        <KeyRow label="Owner" value={
          <span style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
            <EditableCell type="user" value={raw.assigned_user_id || null} users={users} fallbackLabel={raw.assigned_user_name} locked={dcLock('assigned_user_id')} editable={canWrite}
              onSave={(assigned_user_id) => window.saveOwnerWithScope({ kind: 'company', field: 'assigned_user_id', value: assigned_user_id, users, companyName: company.name, save: onSave })} />
            {raw.owner_locked && <window.OwnerLockHint kind="company" />}
          </span>} />
        <KeyRow label="Industry" value={<EditableCell type="select" value={company.industryId} options={[{ value: null, label: '— Unassigned —' }, ...industryOptions]} fallbackLabel={raw.industry_name} locked={dcLock('industry_id')} editable={canWrite} onSave={(industry_id) => onSave({ industry_id })} />}
          after={wand('industry_id', 'industry', company.industryId, (industry_id) => onSave({ industry_id }), raw.industry_name)} />
        <KeyRow label="HQ" value={
          <span style={{ display: 'inline-flex', gap: 4, minWidth: 0, alignItems: 'center' }}>
            <EditableCell type="text" list={window.GEO.LIST_IDS.city} value={raw.hq_city || ''} locked={dcLock('hq_city')} editable={canWrite} onSave={(hq_city) => onSave({ hq_city })} />
            <span style={V2_DIM}>,</span>
            <EditableCell type="text" list={window.GEO.LIST_IDS.state} value={raw.hq_state || ''} locked={dcLock('hq_state')} editable={canWrite} onSave={(hq_state) => onSave({ hq_state })} />
          </span>}
          after={<>{wand('hq_city', 'text', raw.hq_city, (hq_city) => onSave({ hq_city }))}{wand('hq_state', 'text', raw.hq_state, (hq_state) => onSave({ hq_state }))}</>} />
        <KeyRow label="Website" value={<EditableCell type="url" value={raw.website || ''} locked={dcLock('website')} editable={canWrite} onSave={(website) => onSave({ website })} />}
          after={wand('website', 'url', raw.website, (website) => onSave({ website }))} />
        <RailFooter onClick={() => openPanel('details')} />
      </window.Section>
      <PeopleCard contacts={contacts} canWrite={canWrite} onOpen={setContactId} onAdd={onAddContact} onAll={() => openPanel('people')} />
      <window.Section variant="rail" title="Source & status" collapsible id="company.status" preview={statusPreview}>
        <KeyRow label="Revenue" value={<EditableCell type="currency" value={company.revenue} locked={dcLock('revenue_est')} editable={canWrite} onSave={(revenue_est) => onSave({ revenue_est })} />}
          after={wand('revenue_est', 'money', company.revenue, (revenue_est) => onSave({ revenue_est }))} />
        <KeyRow label="EBITDA" value={<EditableCell type="currency" value={company.ebitda} locked={dcLock('ebitda_est')} editable={canWrite} onSave={(ebitda_est) => onSave({ ebitda_est })} />}
          after={wand('ebitda_est', 'money', company.ebitda, (ebitda_est) => onSave({ ebitda_est }))} />
        <KeyRow label="Pipeline state" value={<EditableCell type="select" value={company.pipelineState} options={stateOptions} editable={canWrite} onSave={(pipeline_state) => onSave({ pipeline_state })} />} />
        <window.LeadSourceFields raw={raw} onSave={onSave} Row={KeyRow} withDate withDetail />
        {sellerTools && <KeyRow label="Ownership" value={<OwnershipValue summary={summary} company={company} soldThroughUs={soldThroughUs} onClick={() => openPanel('ownership')} />} />}
        <KeyRow label="Milestones" value={<window.TrackerStageCell company={company} />} after={<window.HelpTip text={MILESTONE_TIP} />} />
        <div style={{ padding: '0 14px 10px' }}><window.SignalsChips signals={company.signals || {}} /></div>
        <RailFooter onClick={() => openPanel('details')} />
      </window.Section>
    </>
  );
};

// ── left: People (the top 5; `extra` = step 12's Team link on a firm) ────────
const PeopleCard = ({ contacts, canWrite = false, onOpen, onAdd, onAll, extra = null }) => {
  const rows = CompanyPageKit.peopleRows(contacts, window.DealPageKit && window.DealPageKit.rankPeople);
  const n = contacts ? contacts.length : null;
  const canOpenAll = window.recordPanelAvailable('people', window.recordSurface());
  const action = (
    <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
      {extra}
      {canWrite && window.NewPersonDrawer && <window.Btn size="xs" variant="outline" icon={<window.Icon.Plus size={10} />} onClick={onAdd}>Add</window.Btn>}
      {canOpenAll && <window.Btn size="xs" variant="ghost" onClick={onAll}>{n == null ? 'All —' : `All ${n}`}</window.Btn>}
    </span>
  );
  return (
    <window.Section variant="rail" title="People" action={action} collapsible id="company.people" preview={n == null ? '—' : `${n} contact${n === 1 ? '' : 's'}`}>
      {contacts == null ? <div style={{ padding: '8px 14px', ...V2_DIM }}>—</div>
        : rows.length === 0 ? <div style={{ padding: '8px 14px', ...V2_DIM }}>0</div>
        : rows.map((c) => {
          const r = c._raw || {};
          return (
            <div key={c.id} data-person-row={c.id} style={V2_ROW}>
              <window.Avatar name={c.name} size={20} color="oklch(0.50 0.13 295)" />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
                  <button type="button" onClick={() => onOpen && onOpen(c.id)} style={{ ...V2_LINK, ...V2_ELLIPSIS, color: 'var(--text)', fontWeight: 500, fontSize: 12.5 }}>{c.name}</button>
                  {r.is_decision_maker && <window.Pill hue="violet" sz="sm">decision-maker</window.Pill>}
                  {r.do_not_call && <window.Pill hue="rose" sz="sm">DNC</window.Pill>}
                </div>
                <div style={{ ...V2_ELLIPSIS, fontSize: 11, color: 'var(--text-3)' }}>{c.title && c.title !== '—' ? c.title : <span style={V2_DIM}>—</span>}</div>
              </div>
              <window.CallButton variant="icon" phone={c.phone} doNotCall={!!r.do_not_call} name={c.name} />
              {c.email && <a href={`mailto:${c.email}`} aria-label={`Email ${c.name}`} style={V2_ICON_LINK}><window.Icon.Mail size={12} /></a>}
              {c.email && window.CopyIconBtn && <window.CopyIconBtn text={c.email} />}
            </div>
          );
        })}
    </window.Section>
  );
};

// ── right: Deals (from CompanyDetail's engagements; no fetch) ────────────────
const DealsCard = ({ engagements, dealEngs, liveEngs, archivedEngs, totalEngFees, buyerEngs, onOpen, onNew }) => {
  const [fold, setFold] = React.useState({ archived: false, shells: false, buying: false });
  const g = CompanyPageKit.dealsGroups({ engagements, dealEngs, liveEngs, archivedEngs, buyerEngs });
  const canWrite = window.recordCanWrite();
  const Row = window.EngagementRow;
  const header = CompanyPageKit.dealsHeader({ engagements, liveEngs, totalEngFees }, window.fmtMoney);
  const n = engagements ? (dealEngs || []).length : null;
  const flip = (k) => setFold((f) => ({ ...f, [k]: !f[k] }));
  return (
    <window.SummaryCard title={`Deals · ${n == null ? '—' : n}`} collapsible id="company.deals" preview={header}
      action={canWrite && onNew && window.NewEngagementDrawer ? <window.Btn size="xs" variant="outline" icon={<window.Icon.Plus size={10} />} onClick={onNew}>New</window.Btn> : null}>
      <div data-deals-header="" className="num" style={V2_SUBHEAD}>{header}</div>
      {engagements == null && <div style={{ padding: '10px 14px' }}><window.SkeletonRows rows={2} variant="engagement" /></div>}
      {Row && g.live.map((e) => <Row key={e.id} e={e} onOpen={onOpen} />)}
      {Row && g.other.map((e) => <Row key={e.id} e={e} onOpen={onOpen} dim />)}
      {g.closed.length > 0 && <div data-deals-closed="" style={V2_SUBHEAD}>{`${g.closed.length} closed`}</div>}
      {Row && g.closed.map((e) => <Row key={e.id} e={e} onOpen={onOpen} dim />)}
      <Fold label={`Archived · ${g.archived.length}`} open={fold.archived} onToggle={() => flip('archived')} hidden={!g.archived.length}>
        {Row && g.archived.map((e) => <Row key={e.id} e={e} onOpen={onOpen} />)}
      </Fold>
      <Fold label={`Deal pursuits · ${g.shells.length}`} open={fold.shells} onToggle={() => flip('shells')} hidden={!g.shells.length}>
        {Row && g.shells.map((e) => <Row key={e.id} e={e} onOpen={onOpen} dim />)}
      </Fold>
      <Fold label={`Buying into · ${g.buying.length}`} open={fold.buying} onToggle={() => flip('buying')} hidden={!g.buying.length}>
        {g.buying.map((b) => <BuyingRow key={b.id} b={b} onOpen={() => onOpen(b.engagement_id)} />)}
      </Fold>
    </window.SummaryCard>
  );
};
// Marketing: Blind ✓/– · PB/PEG ✓/– · Unblinded ✓/– · the blast state (the root's one read) → the Marketing drawer.
const MarketingLine = ({ raw, blast, sellerTools, liveSeller, onOpen }) => {
  if (!CompanyPageKit.showMarketing({ sellerTools, liveSeller, raw, blast })) return null;
  return <LineCard id="company.marketing" title="Marketing" line={CompanyPageKit.marketingLine({ raw, blast }).join(' · ')} onOpen={onOpen} />;
};
// MyExit: the latest run's pills · N runs; at 0 runs the header carries Start / Upload (the root's handlers).
const MyExitLine = ({ companyId, sellerTools, liveSeller, canWrite, onOpen, onStart, onUpload, starting }) => {
  const sum = window.useRecordSummary('company', companyId);
  const s = sum.data || {};
  const m = CompanyPageKit.myexitLine(s.myexit, window.myexitLabel);
  if (!CompanyPageKit.showMyExit({ sellerTools, liveSeller, myexit: s.myexit })) return null;
  const runs = m ? m.runs : 0;
  const line = sum.loading || !m ? '—' : runs === 0 ? '0'
    : m.inProgress ? `In progress · ${runs} run${runs === 1 ? '' : 's'}` : `${m.pills.concat([`${runs} run${runs === 1 ? '' : 's'}`]).join(' · ')}`;
  const action = runs === 0 && canWrite ? (
    <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
      <window.Btn size="xs" variant="ghost" icon={<window.Icon.Doc size={10} />} onClick={onUpload}>Upload</window.Btn>
      <window.Btn size="xs" variant="outline" icon={<window.Icon.Plus size={10} />} onClick={onStart} disabled={!!starting}>Start</window.Btn>
    </span>) : null;
  return <LineCard id="company.myexit" title={runs === 0 ? 'MyExit · 0' : 'MyExit'} line={line} onOpen={onOpen} action={action} />;
};
// Calls: N calls · last <outcome> <day> + the DNC / bad-phone flags → Meetings & calls.
const CallsLine = ({ companyId, onOpen }) => {
  const sum = window.useRecordSummary('company', companyId);
  const c = (sum.data && sum.data.calls) || null;
  if (!CompanyPageKit.showCalls(c)) return null;
  return (
    <LineCard id="company.calls" title="Calls" line={CompanyPageKit.callsLine(c)} onOpen={onOpen}>
      {c.do_not_call && <window.Pill hue="rose" sz="sm">DNC</window.Pill>}
      {c.bad_phone && <window.Pill hue="amber" sz="sm">bad phone</window.Pill>}
    </LineCard>
  );
};
// Files (non-seller companies, firms): the 3 latest through openAttachment; ⤢ → the Files workspace. Hidden at 0.
const FilesCard = ({ companyId, onOpen }) => {
  const sum = window.useRecordSummary('company', companyId);
  const f = (sum.data && sum.data.files) || null;
  if (!f || !f.count) return null;
  const canOpen = window.recordPanelAvailable('files', window.recordSurface());
  return (
    <window.SummaryCard title={`Files · ${f.count}`} collapsible id="company.files" preview={`${f.count} file${f.count === 1 ? '' : 's'}`}
      action={canOpen ? <window.Btn size="xs" variant="ghost" icon={<window.Icon.Maximize size={11} />} onClick={onOpen} /> : null}>
      {(f.latest || []).slice(0, FILES_SHOWN).map((a) => (
        <button key={a.id} type="button" onClick={() => window.AtlasApi.openAttachment(a.id, a.filename)} style={V2_LINE_BTN}>
          <span style={{ ...V2_ELLIPSIS, flex: 1, color: 'var(--text)' }}>{a.filename}</span>
          <span className="num" style={{ fontSize: 11, color: 'var(--text-4)', whiteSpace: 'nowrap' }}>{a.at ? window.fmtDate(a.at) : ''}</span>
        </button>
      ))}
    </window.SummaryCard>
  );
};

// ── the action drawers CompanyDetail's classic layout holds (re-mounted here) ─
const CompanyActionDrawers = ({ companyId, company, buyer, stages, activeUser, isBuyerFirm, sellerDealCtx, drawers, onContactCreated, onEngagementCreated, onRefreshBuyer, z }) => (
  <>
    {drawers.addingContact && window.NewPersonDrawer && (
      <window.NewPersonDrawer initialCompany={{ id: companyId, name: company.name }} zIndex={z}
        onClose={() => drawers.setAddingContact(false)}
        onCreated={(contact) => { drawers.setAddingContact(false); onContactCreated(contact); }} />
    )}
    {drawers.addingEngagement && window.NewEngagementDrawer && (
      <window.NewEngagementDrawer stages={stages} activeUser={activeUser} zIndex={z}
        initialCompany={{ id: companyId, name: company.name }}
        initialBuyer={isBuyerFirm ? { id: companyId, name: company.name } : undefined}
        initialDeal={isBuyerFirm ? undefined : (sellerDealCtx || undefined)}
        onClose={() => drawers.setAddingEngagement(false)}
        onCreated={(eng) => { drawers.setAddingEngagement(false); onEngagementCreated(eng); }} />
    )}
    {/* ── firm variant (step 12): S5 — the ONE SendDealDrawer mount on v2, final form; 12 adds the zIndex passthrough in buyer_detail.jsx and leaves this line alone */}
    {buyer && window.BuyerFirm && (
      <window.BuyerFirm.SendDealDrawer buyer={buyer} open={!!drawers.sendOpen} onClose={() => drawers.setSendOpen(false)} onSent={onRefreshBuyer} zIndex={z} />
    )}
  </>
);

// ── drawer bodies registered in record_panels.jsx (anchor B's details, anchor C) ─
// ?panel=details — the full properties panel; on a firm the investment profile above it and
// only the company's Company / Location / Status / ID groups (no seller-only groups).
const CompanyDetailsBody = ({ api }) => {
  const c = api.ctx || {};
  if (!window.CompanyPropertiesPanel || !api.record) return null;
  return (
    <div style={{ margin: '0 -16px' }}>
      {/* ── firm variant (step 12): S6 — the investment profile above the properties (final form; 12 does not edit it) */}
      {c.buyer && window.BuyerFirm && (
        <window.BuyerFirm.ProfilePanel buyer={api.ctx.buyer} users={api.ctx.users} onSave={api.ctx.saveBuyer} onChange={api.ctx.onBuyerChange} />
      )}
      <window.CompanyPropertiesPanel company={api.record} buyer={c.buyer === undefined ? null : c.buyer} onAddToBuyerList={api.canWrite ? c.onAddToBuyerList : null}
        users={c.users || []} roleOptions={c.roleOptions || []} stateOptions={c.stateOptions || []} industryOptions={c.industryOptions || []}
        onSave={c.saveCompany} onToggleAcquired={c.onToggleAcquired} onToggleDnc={c.onToggleDnc} afterCompany={null} firm={!!(api.facts && api.facts.buyerFirm)} />
    </div>
  );
};
// ?panel=ownership — the ownership trail + the acquisition records (OwnershipPanel), then Mark acquired / Not acquired….
// On a deal the body reads the company itself: Mark acquired asks first while the company has a live deal (the one
// on screen included — close_out_company exits them all as Company sold), then PATCHes and re-pulls the deal, its
// notes and the company (into DealDetail too); its reversal goes through the deal page's drawer (ctx.onReverseOpen).
const OwnershipDrawerBody = ({ api }) => {
  const c = api.ctx || {};
  const isDeal = api.rec.kind === 'deal';
  const [co, setCo] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  React.useEffect(() => {
    if (!isDeal || !api.facts || !api.facts.companyId) return undefined;
    let alive = true;
    window.AtlasApi.getCompany(api.facts.companyId).then((raw) => { if (alive && raw) setCo(window.AtlasData.__adaptCompany(raw)); }).catch(() => {});
    return () => { alive = false; };
  }, [isDeal, api.facts && api.facts.companyId]);   // eslint-disable-line react-hooks/exhaustive-deps
  const Panel = window.OwnershipPanel;
  const company = isDeal ? co : api.record;
  const companyId = isDeal ? (api.facts && api.facts.companyId) : api.rec.id;
  if (!Panel || !companyId) return null;
  const acquired = !!(company && company._raw && company._raw.is_acquired);
  const pullCompany = () => window.AtlasApi.getCompany(companyId)
    .then((raw) => { if (!raw) return; setCo(window.AtlasData.__adaptCompany(raw)); if (c.onCompanyChange) c.onCompanyChange(raw); })
    .catch(() => {});
  const onDecided = c.onAcquisitionDecided || (isDeal ? async () => {
    if (c.reloadRecord) await c.reloadRecord();
    if (c.reloadNotes) c.reloadNotes();
    pullCompany();
  } : undefined);
  const markAcquiredOnDeal = async () => {
    if (busy) return;
    setBusy(true);
    try {
      let live;
      try {
        const res = await window.AtlasApi.listEngagements({ company_id: companyId, limit: 100 });
        live = ((res && res.items) || []).map(window.AtlasData.__adaptEngagement).filter(CompanyPageKit.liveDeal);
      } catch (_) {
        live = CompanyPageKit.liveDeal(api.record) ? [api.record] : [];   // the list failed: the deal on screen is the one we know
      }
      if (live.length && !(await confirmExitLiveDeals(live))) return;
      await window.AtlasApi.updateCompany(companyId, { is_acquired: true });
      if (onDecided) await onDecided();
    } catch (e) {
      window.pushToast({ type: 'error', title: 'Could not save', body: e.message });
    } finally {
      setBusy(false);
    }
  };
  const toggle = isDeal
    ? (next) => (next ? markAcquiredOnDeal() : (c.onReverseOpen ? c.onReverseOpen() : null))
    : c.onToggleAcquired;
  return (
    <div data-ownership-body="" style={{ margin: '0 -16px' }}>
      {company && <Panel companyId={companyId} company={company} buyer={isDeal ? null : (c.buyer === undefined ? null : c.buyer)} onDecided={onDecided} />}
      {api.canWrite && toggle && (
        <div data-ownership-actions="" style={{ display: 'flex', gap: 8, padding: '12px 14px' }}>
          {!acquired && <window.Btn size="sm" variant="outline" onClick={() => toggle(true)} disabled={busy}>Mark acquired</window.Btn>}
          {acquired && <window.Btn size="sm" variant="outline" onClick={() => toggle(false)}>Not acquired…</window.Btn>}
        </div>
      )}
    </div>
  );
};
// ?panel=marketing — the three long texts with their write wands, then the blast control.
const MarketingDrawerBody = ({ api }) => {
  const c = api.ctx || {};
  const company = api.record;
  const rs = window.useFieldResearch('company', api.rec.id);
  if (!company) return null;
  const raw = company._raw || {};
  const dcLock = (f) => (raw.dc_synced_fields || []).includes(f);
  const EditableCell = window.EditableCell;
  return (
    <div data-marketing-body="" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {MARKETING_FIELDS.map(([field, label]) => (
        <div key={field} data-marketing-field={field}>
          <div style={{ fontSize: 11.5, color: 'var(--text-3)', marginBottom: 2, display: 'flex', alignItems: 'center', gap: 5 }}>
            <span>{label}</span>
            {api.canWrite && <window.ResearchField ctl={rs} field={field} label={label} kind="longtext" write value={raw[field]} onApply={(v) => c.saveCompany({ [field]: v })} />}
          </div>
          <div style={{ fontSize: 12 }}>
            <EditableCell type="multiline" label={label} value={raw[field] || ''} locked={dcLock(field)} editable={api.canWrite} onSave={(v) => c.saveCompany({ [field]: v || null })} />
          </div>
        </div>
      ))}
      {window.BlastQueueControl && <div style={{ paddingTop: 6, borderTop: '1px solid var(--border)' }}><window.BlastQueueControl companyId={api.rec.id} /></div>}
    </div>
  );
};
// ?panel=myexit — the exit profile: the seller brief, then every run (open / PDF / delete, Start, Upload).
const MyExitDrawerBody = ({ api }) => {
  const c = api.ctx || {};
  if (!api.record) return null;
  return (
    <div data-myexit-body="" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {window.SellerBriefSection && <window.SellerBriefSection companyId={api.rec.id} sectionId="company.myexit_brief" title="Seller brief" readOnly={!api.canWrite} />}
      {window.MyExitPanel && <div style={{ margin: '0 -16px' }}><window.MyExitPanel companyId={api.rec.id} contacts={c.contacts || []} /></div>}
    </div>
  );
};

// ── the page ──────────────────────────────────────────────────────────────────
const CompanyPageV2 = (p) => {
  const { companyId, company, activeUser, users, contacts, engagements, dealEngs, liveEngs, archivedEngs, totalEngFees, buyerEngs, buyer,
    stages, isBuyerFirm, sellerTools, soldThroughUs, sellerDealCtx, feed, panel, shared, drawers,
    onSave, onSaveBuyer, onBuyerChange, onAddToBuyerList, onToggleAcquired, onAcquisitionDecided, onRefreshBuyer, dealsTick,
    roleOptions, stateOptions, industryOptions, setDealId, setContactId } = p;
  const { facts: panelFacts, variant: panelVariant, ctx: classicCtx } = panel;   // the classic objects, untouched (shapes pinned)
  const sum = window.useRecordSummary('company', companyId);                  // the one root read: last touch + the ownership slots (A1)
  const pp = window.usePanelParam({ kind: 'company', id: companyId });        // a pop-out open → z70 on the page drawers
  const draftOpen = window.useRecordDraftOpen('company', companyId);
  const logDot = window.useRecordDraftFlag('company', companyId);             // Log ▾ dot: a kept draft with the pane closed (A7)
  const blast = useBlastState(sellerTools ? companyId : null);                // null id = no read (non-seller / unknown / firm)
  const [composeRequest, setComposeRequest] = React.useState(null);
  const [uploadOpen, setUploadOpen] = React.useState(false);                  // ⋯ › Upload MyExit…
  const [starting, setStarting] = React.useState(false);                      // ⋯ › Start MyExit
  const canWrite = window.recordCanWrite();
  // ── firm variant (step 12): S0 — the firm facts. `F` is undefined until buyer_firm_v2.jsx ships; then `isFirm`
  // beats the 'company' skeleton even while the roster row is in flight (buyer_id on the payload). No hook here.
  const F = window.BuyerFirmV2;
  const buyerId = company && company._raw && company._raw.buyer_id != null ? company._raw.buyer_id : null;
  const isFirm = !!F && F.isFirm(company, buyer);
  const variantNow = isFirm ? 'buyer_firm' : panelVariant;                    // 'company' | 'buyer_firm' | 'seller' | 'non_seller'
  const onSendDeal = () => drawers.setSendOpen(true);
  // The page's own drawers (New person, New engagement, Not acquired, Upload MyExit, Send a deal) render in the
  // page tree; a reference drawer (z 50, a body portal) or a workspace (z 55) is open → stack them (Z.stacked).
  const z = pp.name ? 70 : undefined;
  const s = sum.data || {};
  const raw = company._raw || {};
  const liveSeller = (liveEngs || []).some((e) => e.type !== 'buyer_buyside');
  const onLog = (kind) => setComposeRequest({ kind, n: Date.now() });
  const openPanel = (name, params) => window.openRecordPanel(name, params || {}, { via: 'button' });
  const K = window.RecordNotesKit;
  const Icon = window.Icon;
  const nav = window.AtlasNav || {};

  // ── MyExit: Start (new tab) / Upload from the ⋯ menu and the card header (one implementation)
  const startMyExit = async () => {
    if (starting) return;
    setStarting(true);
    try {
      const dm = (contacts || []).find((c) => (c._raw && c._raw.is_decision_maker) || c.is_decision_maker);
      const a = await window.AtlasApi.createMyExitAssessment({ company_id: companyId, contact_id: dm ? dm.id : null });
      if (a && a.tool_url) window.open(a.tool_url, '_blank');
      window.refreshRecordSummary('company', companyId);
    } catch (e) {
      window.pushToast({ type: 'error', title: 'Could not start assessment', body: e.message });
    } finally {
      setStarting(false);
    }
  };

  // ── header
  const pillDesc = CompanyPageKit.pills({ roles: raw.roles, isAcquired: !!raw.is_acquired, soldThroughUs,
    pending: s.acquisitions ? s.acquisitions.pending : 0, dnc: !!raw.is_dnc, blast });
  const companyPills = pillDesc.map((d) => {
    if (d.key === 'roles') return <window.RoleChips key="roles" roles={raw.roles} details={ROLE_DETAILS} />;
    if (d.key === 'sold' || d.key === 'acquired') {
      // the ownership pill opens the Ownership drawer (spec "Ownership & acquisition" › Opens from); plain where the drawer does not apply
      const pill = <window.Pill hue={d.key === 'sold' ? 'green' : 'rose'} sz="sm">{d.key === 'sold' ? 'sold through us' : 'acquired'}</window.Pill>;
      return sellerTools
        ? <button key="own" type="button" data-ownership-pill={d.key} onClick={() => openPanel('ownership')} style={V2_PILL_BTN}>{pill}</button>
        : <React.Fragment key="own">{pill}</React.Fragment>;
    }
    if (d.key === 'pending') return (
      <window.AcquisitionHeaderPill key="own" companyId={companyId} companyName={company.name}
        hidden={Boolean(raw.is_acquired)} onDecided={onAcquisitionDecided} drawerZIndex={z} />);   // the third mount (contract §10.9)
    if (d.key === 'dnc') return <window.Pill key="dnc" hue="rose" sz="sm">DNC</window.Pill>;
    // the blast pill opens the Marketing drawer (spec "Company · identity row")
    return (
      <button key={`blast-${d.list}`} type="button" data-blast-pill={d.list} onClick={() => openPanel('marketing')} style={V2_PILL_BTN}>
        <window.Pill hue={d.pushed ? 'blue' : 'amber'} sz="sm">{CompanyPageKit.blastPillText(d)}</window.Pill>
      </button>);
  });
  const companyPrimaries = canWrite ? [
    buyer === null && isBuyerFirm && (
      <window.Btn key="add-buyer" variant="primary" size="sm" icon={<Icon.Plus size={12} />} onClick={onAddToBuyerList} disabled={!!p.addingBuyer}>
        {p.addingBuyer ? 'Adding…' : 'Add to buyer list'}</window.Btn>),
  ] : [];
  const live = CompanyPageKit.liveMeta(liveEngs);
  const liveMeta = live ? (
    <span style={V2_META}>
      <button type="button" data-live-deal="" onClick={() => setDealId(live.id)} style={{ ...V2_LINK, color: 'var(--text-3)', fontSize: 12 }}>{`${live.text} →`}</button>
      {live.more > 0 && <span style={V2_DIM}>{`· +${live.more} live`}</span>}
    </span>) : null;
  const myexitCardShown = CompanyPageKit.showMyExit({ sellerTools, liveSeller, myexit: s.myexit });
  const LABEL = { new_engagement: 'New engagement', add_contact: 'Add contact', send_on_blast: 'Send on blast…', history: 'History',
    mark_acquired: 'Mark acquired…', not_acquired: 'Not acquired…', start_myexit: 'Start MyExit', upload_myexit: 'Upload MyExit…',
    edit_profile: 'Edit investment profile' };
  const ACTION = {
    new_engagement: () => drawers.setAddingEngagement(true), add_contact: () => drawers.setAddingContact(true),
    send_on_blast: () => openPanel('marketing'), history: () => openPanel('history'),
    mark_acquired: () => onToggleAcquired(true), not_acquired: () => onToggleAcquired(false),
    start_myexit: startMyExit, upload_myexit: () => setUploadOpen(true), edit_profile: () => openPanel('details'),
  };
  const menuItems = CompanyPageKit.menuKeys({ variant: variantNow, myexitCardShown }).map((k) => (k === '|' ? { divider: true }
    : (k === 'mark_acquired' && raw.is_acquired) || (k === 'not_acquired' && !raw.is_acquired) ? null
    : { key: k, label: LABEL[k], onSelect: ACTION[k] })).filter(Boolean);
  // ── firm variant (step 12): S1 — every header slot but crumb / logo / title / log / lastTouch comes from BuyerFirmV2 on a firm.
  const firmHdr = isFirm ? F.header({ company, buyer, canWrite, onSendDeal, onAcquisitionDecided, z }) : null;
  const header = {
    crumb: { label: 'Companies', onClick: p.back },
    logo: <window.Logo name={company.name} color={company.logo} size={32} />,
    title: <h1 style={V2_H1}>{company.name}{raw.aka && <span style={V2_AKA}>{' · now '}<span style={{ color: 'var(--text-2)' }}>{raw.aka}</span></span>}</h1>,
    pills: isFirm ? firmHdr.pills : companyPills,
    terminal: null, stage: null,
    primaries: isFirm ? firmHdr.primaries : companyPrimaries,
    log: <window.LogButton onPick={onLog} disabled={!activeUser} dot={logDot} kinds={['note', 'call', 'email', 'meeting', 'task']} />,
    menu: <window.RecordMenu items={isFirm ? F.menuItems({ drawers, canWrite }) : menuItems} />,
    meta: isFirm ? firmHdr.meta : liveMeta,
    lastTouch: sum.loading ? null : (s.last_touch || null),
    banner: null,
    band: isFirm ? <F.Band buyer={buyer} contacts={contacts} /> : null,
  };

  // ── composer (RecordPage's pane; openers go through composeRequest → RecordNotes)
  const attach = { company_id: companyId };
  const links = { company: { id: companyId, name: company.name } };
  const onSaved = (r) => {
    if (r.type === 'task') { window.dispatchEvent(new CustomEvent('task-created')); return; }
    if (feed.reload) feed.reload();
    if (r.stamped && p.onCompanyChange) window.AtlasApi.getCompany(companyId).then(p.onCompanyChange).catch(() => {});
  };
  const composer = {
    open: draftOpen && canWrite,
    element: canWrite ? (
      <window.RecordComposer rec={{ kind: 'company', id: companyId }} attach={attach} engagementPicker
        activeUser={activeUser} members={users} kinds={K.composerKindsFor(false)} presentation="inline" taskLinks={links} onSaved={onSaved} />) : null,
    title: 'Log',
  };

  // ── columns
  // ── firm variant (step 12): left
  const left = isFirm ? (
    <>
      <F.Left company={company} buyer={buyer} users={users} canWrite={canWrite} onSaveBuyer={onSaveBuyer} onSave={onSave} onOpenContact={setContactId} />
      <PeopleCard contacts={contacts} canWrite={canWrite} onAdd={() => drawers.setAddingContact(true)} onOpen={setContactId} onAll={() => openPanel('people')} extra={<F.TeamLink buyer={buyer} />} />
    </>
  ) : variantNow === 'buyer_firm' ? <ColumnSkeleton /> : (
    <CompanyLeft company={company} users={users} contacts={contacts} stateOptions={stateOptions} industryOptions={industryOptions} sellerTools={sellerTools}
      soldThroughUs={soldThroughUs} summary={sum} onSave={onSave} setContactId={setContactId} onAddContact={() => drawers.setAddingContact(true)} openPanel={openPanel} />
  );
  // ── the act column: Open tasks first on every variant (the same slot while the variant is unknown), shown at 0
  // for a writer. Call triggers under it once the roster row is known absent; firm variant (step 12): S3 — on a
  // firm the act column is the tasks card only.
  const act = (
    <>
      {window.CompanyTasksCard && <window.CompanyTasksCard companyId={companyId} companyName={company.name} activeUser={activeUser} tick={0} showEmpty={canWrite} openTitle />}
      {!isFirm && variantNow !== 'company' && buyer === null && window.CompanySignalsSection && (
        <window.CompanySignalsSection companyId={companyId} sectionId="company.signals" marketHere={sellerTools} />
      )}
    </>
  );
  const feedEl = (
    <window.RecordNotes kind="company" recordId={companyId} sectionId="company.notes" composerSlot="external" feed={feed}
      attach={attach} activeUser={activeUser} members={users} hideCompanyId={companyId} engagementPicker label="Notes"
      taskLinks={links} composeRequest={composeRequest} onCompanyChange={p.onCompanyChange} />
  );
  const dealNames = Object.fromEntries((engagements || []).map((e) => [e.id, e.name]));
  const dealsCard = (
    <DealsCard engagements={engagements} dealEngs={dealEngs} liveEngs={liveEngs} archivedEngs={archivedEngs} totalEngFees={totalEngFees} buyerEngs={buyerEngs}
      onOpen={setDealId} onNew={() => drawers.setAddingEngagement(true)} />
  );
  // ── firm variant (step 12): right
  const right = isFirm ? (
    <>
      <F.Right companyId={companyId} buyer={buyer} buyerEngs={buyerEngs} dealsTick={dealsTick} canWrite={canWrite} onOpenDeal={setDealId} onSend={onSendDeal} />
      {/* the firm's own seller deals only: Pursuits owns the 'Buying into' rows */}
      {(dealEngs || []).length > 0 && (
        <DealsCard engagements={engagements} dealEngs={dealEngs} liveEngs={liveEngs} archivedEngs={archivedEngs} totalEngFees={totalEngFees} buyerEngs={[]}
          onOpen={setDealId} onNew={() => drawers.setAddingEngagement(true)} />
      )}
      <FilesCard companyId={companyId} onOpen={() => openPanel('files')} />
      <CallsLine companyId={companyId} onOpen={() => openPanel('calls')} />
      {window.WebsiteVisitsCard && <window.WebsiteVisitsCard companyId={companyId} />}
    </>
  ) : variantNow === 'company' || variantNow === 'buyer_firm' ? <ColumnSkeleton />        // unknown; or a firm before step 12 ships
    : variantNow === 'seller' ? (
      <>
        {dealsCard}
        {window.CompanyBuyersCard && <window.CompanyBuyersCard buyers={p.interestedBuyers} dealNames={dealNames} onOpenDeal={setDealId} onRowUpdate={p.onInterestedRowUpdate} variant="rail" />}
        {window.IntelCard && <window.IntelCard companyId={companyId} sectionId="company.seller_brief" variant="rail" />}
        {window.MarketCard && <window.MarketCard companyId={companyId} sectionId="company.comps" variant="rail" />}
        {window.CompanyVaultSection && <window.CompanyVaultSection companyId={companyId} sectionId="company.vault" panelRecord={{ kind: 'company', id: companyId }} />}
        <MarketingLine raw={raw} blast={blast} sellerTools={sellerTools} liveSeller={liveSeller} onOpen={() => openPanel('marketing')} />
        <MyExitLine companyId={companyId} sellerTools={sellerTools} liveSeller={liveSeller} canWrite={canWrite} onOpen={() => openPanel('myexit')}
          onStart={startMyExit} onUpload={() => setUploadOpen(true)} starting={starting} />
        <CallsLine companyId={companyId} onOpen={() => openPanel('calls')} />
        {window.WebsiteVisitsCard && <window.WebsiteVisitsCard companyId={companyId} />}
      </>
    ) : (
      <>
        {dealsCard}
        <FilesCard companyId={companyId} onOpen={() => openPanel('files')} />
        <CallsLine companyId={companyId} onOpen={() => openPanel('calls')} />
        {window.WebsiteVisitsCard && <window.WebsiteVisitsCard companyId={companyId} />}
      </>
    );

  // ── the folded-column strips (each icon opens that card's drawer, or its first row)
  const firstLiveSeller = (liveEngs || []).find((e) => e.type !== 'buyer_buyside') || null;
  const railOpen = (it) => {
    if (it.panel) return () => openPanel(it.panel);
    if (it.key === 'company.deals') return () => { if (liveEngs && liveEngs[0]) setDealId(liveEngs[0].id); };
    if (it.key === 'company.interested_buyers') return () => { if (firstLiveSeller && nav.goToDeal) nav.goToDeal(firstLiveSeller.id, { panel: 'buyers' }); };
    return () => {};
  };
  const railItem = (it) => ({ key: it.key, icon: Icon[it.icon] ? React.createElement(Icon[it.icon], { size: 13 }) : null, label: it.label, onOpen: railOpen(it) });
  const LEFT_RAIL = [
    { key: 'key_facts', icon: <Icon.Building size={13} />, label: isFirm ? 'Investment profile' : 'Key facts', onOpen: () => openPanel('details') },
    { key: 'people', icon: <Icon.Users size={13} />, label: 'People', onOpen: () => openPanel('people') },
    { key: 'status', icon: <Icon.Tag size={13} />, label: 'Source & status', onOpen: () => openPanel('details') }];
  const rails = {
    left: isFirm ? LEFT_RAIL.slice(0, 2) : LEFT_RAIL,                            // a firm's left column has no Source & status
    // ── firm variant (step 12): rails.right (Deals only when the column has its Deals card)
    right: isFirm ? F.rails({ buyer }).concat(CompanyPageKit.railItems('non_seller').filter((it) => it.key !== 'company.deals' || (dealEngs || []).length > 0).map(railItem))
      : CompanyPageKit.railItems(variantNow).map(railItem),
  };
  // S5: the facts spread and the ctx the drawer bodies read (contract §1.5 + plan_12's five keys).
  const panelCtx = { ...classicCtx, saveCompany: onSave, saveBuyer: onSaveBuyer, onBuyerChange, buyer, buyerId, users, activeUser, engagements, onToggleAcquired,
    onAcquisitionDecided, onAddToBuyerList, roleOptions, stateOptions, industryOptions, dealsTick, onSendDeal, sellerTools, setContactId, setDealId };
  return (
    <>
      <window.RecordPage kind="company" id={companyId} header={header} left={left} act={act} feed={feedEl} right={right}
        cardIds={isFirm ? F.CARD_IDS : CompanyPageKit.variantCards(variantNow)} rails={rails} composer={composer}
        panel={{ record: company, facts: { ...panelFacts, buyerFirm: panelFacts.buyerFirm || buyerId != null }, variant: variantNow, ctx: panelCtx }}
        onNoteChange={feed.reload} />
      {shared.reverseDrawer ? React.cloneElement(shared.reverseDrawer, { zIndex: z }) : null}
      <CompanyActionDrawers companyId={companyId} company={company} buyer={buyer} stages={stages} activeUser={activeUser} isBuyerFirm={isBuyerFirm}
        sellerDealCtx={sellerDealCtx} drawers={drawers} onContactCreated={p.onContactCreated} onEngagementCreated={p.onEngagementCreated}
        onRefreshBuyer={onRefreshBuyer} z={z} />
      {uploadOpen && window.MyExitUploadDrawer && (
        <window.MyExitUploadDrawer companyId={companyId} contacts={contacts} zIndex={z} onClose={() => setUploadOpen(false)}
          onDone={() => { setUploadOpen(false); window.refreshRecordSummary('company', companyId); }} />
      )}
    </>
  );
};

Object.assign(window, { CompanyPageV2, CompanyPageKit, CompanyDetailsBody, OwnershipDrawerBody, MarketingDrawerBody, MyExitDrawerBody });
